import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { FadeLoader } from 'react-spinners';
import { styledTheme } from 'globalStyles/theme';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import objectSupport from 'dayjs/plugin/objectSupport';
import * as customParseFormat from 'dayjs/plugin/customParseFormat';
import LocationSelect from 'components/LocationSelect';

import FormItem from 'components/FormFields/FormItem';
import FormSelect from 'components/FormFields/FormSelect';
import APFormGroup from 'components/FormFields/APFormGroup';
import InputField from 'components/FormFields/InputField';
import HeaderString from 'components/HeaderString';
import Clickwrap from 'components/Clickwrap';
import CalendarWidget from 'components/FormFields/CalendarWidget';
import ImageUploadField from 'components/FormFields/ImageUploadField';
import APModal from 'components/Modal/APModal';
import {
  createLoginCalendar as createLoginCalendarReduxAction,
} from 'containers/App/actions';
import {
  TIME_ZONES,
  TIME_ZONES_ORDER,
  CURRENCIES,
  DATE_FORMATS,
  TIME_FORMATS,
} from 'utils/constants';
import { prepareLocationForApi } from 'utils/location';
import { useAction } from 'hooks/useAction';

import {
  createProject,
  updateProject,
  fetchOrgWeddingTemplates,
} from 'apollo/projects.query';
import { getSyncedWeddings, toggleCalendarExternalSync } from 'api/calendar';
import { fetchLeadCustomPreferences } from 'apollo/leads.query';
import { timeFormatValidation, getTimeList, compareTime } from 'utils/time';
import TimeSettingFormItem from 'components/FormFields/TimeSettingFormItem';
import { getCurrencySettings } from './utils/currency';
import ChecklistDatesModal from './ChecklistDates';
import ErrorModal from './ErrorModal';
import './styles.scss';

const PROJECT_TYPES = [
  {
    value: 'wedding',
    label: 'Wedding',
  },
  {
    value: 'other',
    label: 'Event or Project',
  },
];

const PROJECT_PARTY_ROLES = [
  { value: 'bride', label: 'Bride' },
  { value: 'groom', label: 'Groom' },
  { value: 'guest-of-honor', label: 'Guest of Honor' },
  { value: 'partner', label: 'Partner' },
  { value: 'bridegroom', label: 'Bridegroom' },
];

const PROJECT_FEATURES = [
  {
    id: 'checklist_enabled',
    name: 'Checklist',
  },
  {
    id: 'calendar_enabled',
    name: 'Calendar',
  },
  {
    id: 'timeline_enabled',
    name: 'Timeline',
  },
  {
    id: 'design_studio_enabled',
    name: 'Design Studio',
  },
  {
    id: 'guests_enabled',
    name: 'Guests & Seating',
  },
  {
    id: 'vendors_enabled',
    name: 'Contacts',
  },
  {
    id: 'budget_enabled',
    name: 'Budget',
  },
  {
    id: 'notes_enabled',
    name: 'Notes',
  },
  {
    id: 'websites_enabled',
    name: 'Websites',
  },
];

const DEFAULT_DATE_FORMAT = 'MM/DD/YYYY';

const ProjectsSettingsDetails = ({
  type,
  weddingId,
  formData,
  projectCreateStartCallback,
  projectCreatedCallback,
  projectCreatedErrorCallback,
  updateWeddingChanges,
  pushNotification,
}) => {
  dayjs.extend(customParseFormat);
  dayjs.extend(objectSupport);
  const userInfo = useSelector((state) => state.global.userInfo);
  const { personas, organization, integrations, id: loginId } = userInfo;
  const {
    id: orgId,
    currency: orgCurrency,
    datePreference,
    timePreference,
    allowedWeddingCount,
  } = organization || {};
  const { integratedStatus } = personas || {};
  const validIntegration = integratedStatus === 'connected' ?
    integrations.find((item) => item.grantStatus === 'valid') : null;
  const displayProvider = validIntegration ?
    `${validIntegration.provider.charAt(0).toUpperCase()}${validIntegration.provider.slice(1)}` :
    null;

  const profileImg = formData?.profileImageUrls?.full?.url;

  const [projectType, setProjectType] = useState(type ? type === 1 ? 'wedding' : 'other' : '');
  const [projectTemplate, setProjectTemplate] = useState('');
  const [templatesWedding, setTemplatesWedding] = useState([]);
  const [templatesOther, setTemplatesOther] = useState([]);

  const [projectName, setProjectName] = useState(formData?.name || '');
  const [role1, setRole1] = useState(formData?.party[0][0] || 'bride');
  const [role2, setRole2] = useState(formData?.party[1][0] || 'groom');
  const [firstName1, setFirstName1] = useState(formData?.party[0][1] || '');
  const [firstName2, setFirstName2] = useState(formData?.party[1][1] || '');
  const [lastName1, setLastName1] = useState(formData?.party[0][2] || '');
  const [lastName2, setLastName2] = useState(formData?.party[1][2] || '');

  const [leadId, setLeadId] = useState(null);

  const [requiredFields, setRequiredFields] = useState({});

  const [originalProfileImageKey, setOriginalProfileImageKey] = useState(null);
  const [profileImageUrl, setProfileImage] = useState(profileImg ? `https://${profileImg.replace('//', '')}` : null);
  const [profileImageKey, setProfileImageKey] = useState(formData?.profileImage?.key || null);

  const [guestCount, setGuestCount] = useState(formData?.estimatedGuestCount || '');
  const [guestCountView, setGuestCountView] = useState(formData?.estimatedGuestCount || '0');
  const [budget, setBudget] = useState(formData?.estimatedBudget ? parseInt(formData.estimatedBudget, 10) : '');
  const [budgetView, setBudgetView] = useState('');

  const [venueName, setVenueName] = useState(formData?.venueName || '');
  const [serviceType, setServiceType] = useState(formData?.serviceTypeId || '');
  const [serviceTypes, setServiceTypes] = useState([]);

  const [syncCalendarOnCreation, setSyncCalendarOnCreation] = useState(false);
  const [isSynced, setIsSynced] = useState(false);

  const createCalendarWithRedux = useAction(createLoginCalendarReduxAction);

  // Support legacy location address if google place location doesn't exist.
  const _location = useMemo(() => {
    if (!formData?.location && formData?.address) {
      let legacyLocation = '';
      const { city, region } = formData.address;
      if (city && city !== '') {
        legacyLocation += city;
      }
      if (region && region !== '') {
        if (legacyLocation !== '') legacyLocation += ', ';
        legacyLocation += region;
      }
      return { formattedAddress: legacyLocation };
    }
    return formData?.location;
  }, [formData]);
  const [location, setLocation] = useState(_location || {});
  const [timeZone, setTimeZone] = useState(formData?.timezone || '');

  const [features, setFeatures] = useState(
    PROJECT_FEATURES.map((field) => {
      return {
        ...field,
        checked: true,
      };
    }),
  );

  const [currency, setCurrency] = useState(formData?.currency || orgCurrency || 'USD');
  const [currencySettings, setCurrencySettings] = useState(formData?.currency ?
    getCurrencySettings(formData?.currency) :
    {
      symbol: '',
      groupSeparator: '',
      decimalSeparator: '',
    },
  );
  const [dateFormat, setDateFormat] = useState(datePreference || 'mm/dd/yyyy');
  const [dateFormatLabel, setDateFormatLabel] = useState(
    DATE_FORMATS.find((format) => format.value === dateFormat)?.label ?? DEFAULT_DATE_FORMAT,
  );
  const [timeFormat, setTimeFormat] = useState(timePreference || '12');
  const [originalProjectDate, setOriginalProjectDate] = useState(
    formData?.weddingDate ?
      dayjs(formData?.weddingDate, 'YYYY-MM-DD').format(DEFAULT_DATE_FORMAT) :
      dayjs().format(DEFAULT_DATE_FORMAT),
  );
  const [eventDate, setEventDate] = useState(formData?.weddingDate);
  const [showDateError, setShowDateError] = useState(false);
  const [timeList, setTimeList] = useState(getTimeList(timeFormat));
  const [allDay, setAllDay] = useState(formData?.allDay ?? true);
  const [startTime, setStartTime] = useState(formData?.startTime || null);
  const [endTime, setEndTime] = useState(formData?.endTime || null);
  // This is only necessary to use in the dependency array of the effect
  // that calls the updateEvent function so that we can update the calendar
  // variables without firing multiple saves.
  const [calendarEvent, setCalendarEvent] = useState({
    startTime: formData?.allDay || null,
    endTime: formData?.endTime || null,
    allDay: formData?.allDay ?? true,
  });
  const [timeInputsCounter, setTimeInputsCounter] = useState(0);
  const [error, setError] = useState('');
  const [isFormValid, setFormValid] = useState(true);
  const [openChecklistDates, setOpenChecklistDates] = useState(false);
  const [isChecklistDatesUpdate, setChecklistDatesUpdate] = useState(false);
  const [openErrorModal, setOpenErrorModal] = useState(false);
  const [errorModalMessage, setErrorModalMessage] = useState(null);
  const [isFormLoaded, setFormLoaded] = useState(false);
  const [openPermissionsErrorModal, setOpenPermissionsErrorModal] = useState(false);

  const featuresColumnLen = 3;

  const defaultWeddingTemplates = [
    {
      value: '',
      label: '',
    },
    {
      value: '__default__',
      label: 'Aisle Planner Wedding Default',
    },
  ];

  const defaultOtherTemplates = [
    {
      value: '',
      label: '',
    },
    {
      value: '__default__',
      label: 'Aisle Planner Event Default',
    },
  ];

  useEffect(() => {
    const getTemplates = async () => {
      const { organizationWeddingTemplates } = await fetchOrgWeddingTemplates();
      if (!organizationWeddingTemplates) return;

      const sortedTemplatesWedding = organizationWeddingTemplates
        .filter((template) => template.templateType === 'wedding')
        .sort((template1, template2) => {
          if (template1.label < template2.label) return -1;
          if (template1.label > template2.label) return 1;
          return 0;
        });
      setTemplatesWedding([...defaultWeddingTemplates, ...sortedTemplatesWedding]);
      const sortedTemplatesOther = organizationWeddingTemplates
        .filter((template) => template.templateType === 'other')
        .sort((template1, template2) => {
          if (template1.label < template2.label) return -1;
          if (template1.label > template2.label) return 1;
          return 0;
        });

      setTemplatesOther([...defaultOtherTemplates, ...sortedTemplatesOther]);
    };
    const getSyncedWedding = async () => {
      try {
        const syncedWeddings = await getSyncedWeddings();
        const thisWedding = syncedWeddings.data.find(w => w.wedding_id === Number(weddingId));
        setIsSynced(!!thisWedding);
      } catch (e) {
        // eslint-disable-next-line
        console.error('Failed to fetch synced wedding', e);
      }
    };
    getSyncedWedding();
    getTemplates();
  }, []);

  const getTemplateValue = () => {
    if (projectType === 'wedding' && templatesWedding?.length === 0) {
      return '__default__';
    }
    if (projectType === 'other' && templatesOther?.length === 0) {
      return '__default__';
    }
    return projectTemplate ?? 0;
  };

  const getTemplateItems = () => {
    if (projectType === 'wedding') {
      return templatesWedding.length === 0 ? defaultWeddingTemplates : templatesWedding;
    }
    return templatesOther.length === 0 ? defaultOtherTemplates : templatesOther;
  };

  useEffect(() => {
    if (type === 1) {
      setProjectType('wedding');
    } else if (type === 2 || type === 3) {
      setProjectType('other');
    }
  }, [type]);

  useEffect(() => {
    if (weddingId !== null && isFormLoaded) {
      projectSettingsUpdate();
    }
  }, [
    role1,
    role2,
    serviceType,
    profileImageKey,
    timeZone,
    guestCount,
    features,
    currency,
    dateFormat,
    timeFormat,
    location,
    calendarEvent,
  ]);

  useEffect(() => {
    setTimeList(getTimeList(timeFormat));
  }, [timeFormat]);

  const updateEventDate = (data) => {
    // https://regex101.com/r/32xeNv/1
    // eslint-disable-next-line
    const regex = new RegExp(/^(0[1-9]|1[012]|[1-9])[\-\/](3[0-1]|[1-2]\d|0?[1-9])[\-\/](\d{4})|(\d{4})[\-\/](0[1-9]|1[012]|[1-9])[\-\/](3[0-1]|[1-2]\d|0?[1-9])|(3[0-1]|[1-2]\d|0?[1-9])[\-\/](0[1-9]|1[012]|[1-9])[\-\/](\d{4})$/);
    if (!regex.test(data)) {
      return false;
    }
    const matches = data.match(regex);
    let year;
    let month;
    let day;

    switch (dateFormatLabel) {
      case 'MM/DD/YYYY':
        year = Number(matches[3] || matches[4] || matches[9]);
        month = Number(matches[1] || matches[5] || matches[7]) - 1;
        day = Number(matches[2] || matches[6] || matches[8]);
        break;
      case 'DD/MM/YYYY':
        year = Number(matches[3] || matches[4] || matches[9]);
        month = Number(matches[2] || matches[5] || matches[8]) - 1;
        day = Number(matches[1] || matches[6] || matches[7]);
        break;
      case 'YYYY/MM/DD':
        year = Number(matches[1] || matches[4] || matches[7]);
        month = Number(matches[2] || matches[5] || matches[8]) - 1;
        day = Number(matches[3] || matches[6] || matches[9]);
        break;
      default:
        year = Number(matches[3] || matches[4] || matches[9]);
        month = Number(matches[1] || matches[5] || matches[7]) - 1;
        day = Number(matches[2] || matches[6] || matches[8]);
    }
    setEventDate({ year, month, day });

    return true;
  };

  // Need to make sure start time isn't later than end time so we want to handle
  // time updates for both start and end in one go, then update a separate state variable
  // that will fire the update function. Also, we want to clear the start/end times
  // if the event is all day so we don't have dangling data.
  const handleTimeChange = ({ start, end, allDay: _allDay }) => {
    let _start = startTime;
    let _end = endTime;
    let newAllDay = allDay;

    if (start) {
      const time = timeFormatValidation(start);
      if (time) {
        setStartTime(time);
        _start = time;
      } else {
        resetTimeInputs();
      }
      // If new start time is after end time.
      if (!endTime || compareTime(_start, endTime, timeFormat) < 1) {
        const trimStartTime = time.split(':');
        const nextHour = (Number(trimStartTime[0]) + 1)%24;
        const newEndTime = `${nextHour}:${trimStartTime[1]}:00`;
        setEndTime(newEndTime);
        _end = newEndTime;
      }
      setCalendarEvent({ startTime: _start, endTime: _end, allDay: newAllDay });
      return;
    }
    if (end) {
      const time = timeFormatValidation(end);
      if (time) {
        setEndTime(time);
        _end = time;
      } else {
        resetTimeInputs();
      }
      // If startTime is after new end time.
      if (!startTime || compareTime(startTime, _end, timeFormat) < 1) {
        const trimEndTime = time.split(':');
        const prevHour = (Number(trimEndTime[0]) - 1)%24;
        const newStartTime = `${prevHour}:${trimEndTime[1]}:00`;
        setStartTime(newStartTime);
        _start = newStartTime;
      }
      setCalendarEvent({ startTime: _start, endTime: _end, allDay: newAllDay });
      return;
    }
    if (_allDay !== false) {
      // Reset start and end time if allDay is unchecked.
      setStartTime(null);
      setEndTime(null);
      _start = null;
      _end = null;
      newAllDay = true;
    } else {
      setStartTime('09:00:00');
      setEndTime('10:00:00');
      _start = '09:00:00';
      _end = '10:00:00';
      newAllDay = false;
    }
    setAllDay(newAllDay);
    setCalendarEvent({ startTime: _start, endTime: _end, allDay: newAllDay });
  };

  const resetTimeInputs = () => {
    setTimeInputsCounter(timeInputsCounter + 1);
  };

  const autoSaveHandler = () => {
    if (weddingId !== null && isFormLoaded) {
      projectSettingsUpdate();
    }
  };

  const projectSettingsUpdate = async () => {
    if (!weddingId) return;
    const weddingBody = {
      name: projectName,
      party: [
        [role1, firstName1, lastName1],
        [role2, firstName2, lastName2],
      ],
      project_type: projectType,
      client: { venue_name: venueName },
      calendar_event: {
        all_day: allDay,
        start_time: allDay ? null : startTime,
        end_time: allDay ? null : endTime,
      },
      profile_image_key: profileImageKey,
      wedding_date: dayjs(eventDate).format('YYYY-MM-DD'),
      location: location ? prepareLocationForApi(location) : {},
      timezone: timeZone,
      estimated_guest_count: !guestCount ? null : guestCount,
      features: Object.assign(
        {},
        ...features.map((feature) => ({ [feature.id]: feature.checked })),
      ),
      currency,
      date_preference: dateFormat,
      time_preference: timeFormat,
    };

    try {
      const data = await updateProject({ id: weddingId, weddingBody });

      if (data) {
        if (updateWeddingChanges) updateWeddingChanges(data?.updateWedding?.wedding);
        if (pushNotification) {
          pushNotification({
            message: 'Project setting was automatically saved.',
            time: 3000,
          });
        }

        setRequiredFields({
          projectName,
          firstName1,
          firstName2,
          lastName1,
          lastName2,
        });
        return true;
      }
    } catch (err) {
      setErrorModalMessage(
        "Sorry, we couldn't save your latest changes, please try again.",
      );
      setOpenErrorModal(true);
      return false;
    }

    // Always return something regardless of what happens.
    return false;
  };

  useEffect(() => {
    if (formData) {
      updateForm();
      setRequiredFields({
        projectName: formData.name,
        firstName1: formData.party[0][1],
        firstName2: formData.party[1][1],
        lastName1: formData.party[0][2],
        lastName2: formData.party[1][2],
      });
      setTimeout(() => {
        setFormLoaded(true);
      }, 1000);
    } else {
      setFormLoaded(true);
    }
  }, [formData]);

  const updateForm = () => {
    const newFormatLabel = DATE_FORMATS.find((format) => format.value === formData?.datePreference)?.label ??
      'MM/DD/YYYY';
    setProjectType(formData?.projectType || '');
    setProjectName(formData?.name || '');

    setRole1(formData?.party[0][0] || 'bride');
    setRole2(formData.party[1][0] || 'groom');
    setFirstName1(formData?.party[0][1] || '');
    setFirstName2(formData?.party[1][1] || '');
    setLastName1(formData?.party[0][2] || '');
    setLastName2(formData?.party[1][2] || '');

    setProfileImage(
      profileImg ? `https://${profileImg.replace('//', '')}` : null,
    );
    setProfileImageKey(formData?.profileImage?.key || null);
    setOriginalProfileImageKey(formData?.originalProfileImage?.key || null);

    setGuestCount(formData?.estimatedGuestCount || '');
    updateGuestCountView(formData?.estimatedGuestCount || '0');

    updateEventDate(formData?.weddingDate);
    setOriginalProjectDate(dayjs(formData?.weddingDate, 'YYYY-MM-DD').format(DEFAULT_DATE_FORMAT));

    setLocation(_location || {});
    setTimeZone(formData?.timezone || '');
    setVenueName(formData?.venueName || '');
    setServiceType(formData?.serviceTypeId || '');
    setFeatures(
      PROJECT_FEATURES.map((field) => {
        const checked =
          formData?.features?.find((feature) => field.id === feature.id)
            ?.checked ?? true;

        return {
          ...field,
          checked,
        };
      }),
    );
    const currentCurrency = formData?.currency || orgCurrency || 'USD';
    setCurrency(currentCurrency);
    if (formData?.estimatedBudget) {
      const _estimatedBudget = parseInt(formData.estimatedBudget, 10);
      setBudget(_estimatedBudget);
      updateCurrencySettings(currentCurrency);
      updateBudgetView(_estimatedBudget, currencySettings);
    }
    setDateFormat(formData?.datePreference || 'mm/dd/yyyy');
    setDateFormatLabel(newFormatLabel);
    setTimeFormat(formData?.timePreference || '12');
    setAllDay(formData?.allDay ?? true);
    setStartTime(formData?.startTime || null);
    setEndTime(formData?.endTime || null);
    if (formData?.leadId) {
      setLeadId(formData.leadId);
    }
  };

  useEffect(() => {
    function setDefaultTemplate(data) {
      // Defaults have 2 items, default template and empty(?).
      // If the empty item needs to be removed, this conditional
      // needs to change.
      if (data.length < 3) {
        setProjectTemplate('__default__');
      } else {
        setProjectTemplate(0);
      }
    }

    if (projectType === 'wedding') {
      setDefaultTemplate(templatesWedding);
    } else {
      setDefaultTemplate(templatesOther);
    }
  }, [projectType, templatesWedding, templatesOther]);

  useEffect(() => {
    const fetchCustomPrefs = async () => {
      const items = await fetchLeadCustomPreferences();
      if (items && items.data) {
        const { leadCustomPreferences } = items.data;
        const filteredLeadCustomPreferences = leadCustomPreferences
          .filter((pref) => pref.kind === 'service-type' && pref.visibility)
          .map((pref) => {
            const value = pref.id;
            const label = pref.name;
            return {
              value,
              label,
            };
          });
        filteredLeadCustomPreferences.unshift({
          value: -1,
          label: 'Click here to add and manage service types in your lead settings',
        });
        setServiceTypes(filteredLeadCustomPreferences);
      }
    };
    fetchCustomPrefs();

    /**
     * #TODO: Refactor this so the settings are only resaved
     * if the data is changed, rather than always on close.
     * Or change so that data is only saved on close rather than any field change.
     */
    // Call save when modal is dismissed and data is changed.
    return () => {
      autoSaveHandler();
    };
  }, []);

  const onFeaturesCheckChange = (index) => {
    const newFeatures = [...features];
    const { checked } = features[index];
    newFeatures[index].checked = !checked;
    setFeatures(newFeatures);
  };

  const onGuestCountViewChange = () => {
    const newGuestCount = Number(guestCountView.replace(/\D/g, ''));
    setGuestCount(newGuestCount);
    updateGuestCountView(newGuestCount);
  };

  const updateGuestCountView = (newGuestCount) => {
    let newGuestCountView;
    if (newGuestCount) {
      newGuestCountView = newGuestCount.toLocaleString('en-US', {
        maximumFractionDigits: 0,
      });
    } else {
      newGuestCountView = '0';
    }
    setGuestCountView(newGuestCountView);
  };

  const onBudgetViewChange = () => {
    let newBudget;
    if (budgetView) {
      newBudget = parseFloat(
        budgetView
          .replace(/[^0-9.,]/g, '')
          .replace(new RegExp(`\\${currencySettings.groupSeparator}`, 'g'), '')
          .replace(
            new RegExp(`\\${currencySettings.decimalSeparator}`, 'g'),
            '.',
          )
          .replace(/^(\d*\.?)|(\d*)\.?/g, '$1$2'),
      );
    } else {
      newBudget = '';
    }

    setBudget(newBudget);
    updateBudgetView(newBudget, currencySettings);
  };

  const updateBudgetView = (newBudget, newCurrencySettings) => {
    let newBudgetView;

    if (newBudget) {
      newBudgetView = newBudget
        .toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
        .replace(
          /[.,]/g,
          (charactersToReplace) =>
          ({
            ',': newCurrencySettings.groupSeparator,
            '.': newCurrencySettings.decimalSeparator,
          }[charactersToReplace]),
        );

      newBudgetView = `${newCurrencySettings.symbol}${newBudgetView}`;
    } else {
      newBudgetView = '';
    }
    setBudgetView(newBudgetView);
  };

  const updateCurrencySettings = (_currency) => {
    const newCurrencySettings = getCurrencySettings(_currency);
    setCurrencySettings(newCurrencySettings);
    updateBudgetView(budget, newCurrencySettings);
  };

  useEffect(() => {
    if (currency) updateCurrencySettings(currency);
  }, [currency, budget]);

  // #TODO: This probably doesn't need to be an effect hook. Can just be a function that is called.
  useEffect(() => {
    if (dayjs(eventDate).isValid()) {
      if (
        isChecklistDatesUpdate &&
        !!weddingId &&
        isFormLoaded &&
        originalProjectDate !== dayjs(eventDate).format(DEFAULT_DATE_FORMAT)
      ) {
        const projectSettingsUpdated = projectSettingsUpdate() || false;
        if (projectSettingsUpdated) {
          setOpenChecklistDates(true);
        } else {
          // Reset form fields if update fails.
          updateEventDate(originalProjectDate);
          setStartTime(formData.startTime || '09:00:00');
          setEndTime(formData.endTime || '10:00:00');
        }
      }
    }
    else {
      updateEventDate(originalProjectDate);
    }
    setChecklistDatesUpdate(false);
  }, [isChecklistDatesUpdate]);

  useEffect(() => {
    if (!eventDate) return;
    if (isChecklistDatesUpdate && Object.keys(eventDate).length) {
      updateEventDateView(dateFormatLabel);
    }
  }, [eventDate, isChecklistDatesUpdate]);

  const onDateViewChange = (date) => {
    setShowDateError(!updateEventDate(date));
  };

  const updateEventDateView = (eventDateFormat) => {
    const eventDateView = document.getElementById('Event Date');
    if (eventDateView) {
      eventDateView.value = dayjs(eventDate).format(eventDateFormat);
    }
  };

  const handleProjectSettingsCreateValidation = () => {
    if (
      !projectTemplate ||
      (projectType === 'wedding' &&
        (!firstName1 || !firstName2 || !lastName1 || !lastName2)) ||
      (projectType === 'other' && !projectName) ||
      !timeZone || !allDay && (!startTime || !endTime)
    ) {
      setError('Please complete all required fields above.');
      setFormValid(false);
      return false;
    }
    setError('');
    setFormValid(true);
    return true;
  };
  const createCalendarIfNotExist = async () => {
    // Need to create the calendar and then sync
    // TODO: This constant should be imported and maintained in one place.
    const calendarName = 'Booked Projects';

    const matchedCalendar = validIntegration?.loginCalendars
      .find(calendar => calendar.name === calendarName);

    if (!matchedCalendar) {
      await createCalendarWithRedux({
        integrationId: validIntegration.id,
        calendarType: 'projects',
        name: calendarName,
      });
    }
  };

  const handleProjectSettingsCreate = async (e) => {
    e.currentTarget.disabled = true;
    if (handleProjectSettingsCreateValidation()) {
      const weddingBody = {
        project_type: projectType,
        wedding_template_id:
          projectTemplate === '' || projectTemplate === '__default__' || projectTemplate === 0
            ? null
            : projectTemplate,
        name: projectName !== '' ? projectName : `${firstName1} & ${firstName2}'s Wedding`,
        party: [
          [role1, firstName1, lastName1],
          [role2, firstName2, lastName2],
        ],
        profile_image_key: profileImageKey,
        calendar_event: {
          all_day: allDay,
          start_time: allDay ? null : startTime,
          end_time: allDay ? null : endTime,
        },
        wedding_date: dayjs(eventDate).format('YYYY-MM-DD'),
        location: location ? prepareLocationForApi(location) : {},
        timezone: timeZone,
        estimated_guest_count: !guestCount ? null : guestCount,
        estimated_budget: !budget ? null : budget * 100,
        features: Object.assign(
          {},
          ...features.map((feature) => ({ [feature.id]: feature.checked })),
        ),
        currency,
        date_preference: dateFormat,
        time_preference: timeFormat,
        organization_id: orgId,
        is_destination: false,
        ... leadId && { lead_id: leadId },
      };

      // Payload to create a new lead with the project.
      if (!leadId) {
        const lead = {
          project_type: projectType,
          contact_first_name: firstName1 || '',
          contact_last_name: lastName1 || '',
          project_name: weddingBody.name,
          notes: '',
          role: '',
          venue_name: venueName,
          timezone: timeZone,
          wedding_date: weddingBody.wedding_date,
          date_inquiry: dayjs().format('YYYY-MM-DD'),
          estimated_guest_count: weddingBody.estimated_guest_count,
          estimated_budget: weddingBody.estimated_budget,
          contact: { addresses: [], emails: [], phones: [] },
          service_type_id: serviceType || null,
          lead_source_id: null,
          last_contacted_date: dayjs().format('YYYY-MM-DD'),
          all_day: allDay,
          start_time: weddingBody.start_time,
          end_time: weddingBody.end_time,
          party: weddingBody.party,
        };
        weddingBody.new_lead = lead;
      }

      try {
        if (projectCreateStartCallback) {
          projectCreateStartCallback();
        }
        const data = await createProject({ weddingBody });
        if (data && projectCreatedCallback) {
          const newData = {
            ...data,
            customTemplate: !(projectTemplate === '' || projectTemplate === '__default__' || projectTemplate === 0),
          };
          projectCreatedCallback(newData);
        }
        if (data && syncCalendarOnCreation && validIntegration) {
          await createCalendarIfNotExist();

          await toggleCalendarExternalSync({
            weddingId: data.createWedding.wedding.id,
            loginId,
            checked: true,
          });
        }
      } catch (err) {
        if (projectCreatedErrorCallback) {
          projectCreatedErrorCallback({ ...err, organization: { allowedWeddingCount } });
          e.target.disabled = false;
          return;
        }
        if (err.message === 'Wedding limit') {
          setErrorModalMessage(`Sorry, your organization has used all ${allowedWeddingCount}
          active projects that your current plan allows.
          To create a new project you must first either archive a project or upgrade your plan.`);
        } else {
          setErrorModalMessage(
            "Sorry, we couldn't create the project.  Please try again.",
          );
        }
        setOpenErrorModal(true);
      }
    } else {
      e.currentTarget.disabled = false;
    }
  };

  const handleCalendarSync = async () => {
    if (weddingId) {
      await createCalendarIfNotExist();
      const resp = await toggleCalendarExternalSync({
        weddingId,
        loginId,
        checked: !isSynced,
      });
      const syncedStatus = resp?.data?.integrations_sync_enabled;
      setIsSynced(!!syncedStatus);
      if (pushNotification) {
        pushNotification({
          message: 'Project setting was automatically saved.',
          time: 3000,
        });
      }
    } else {
      setSyncCalendarOnCreation(!syncCalendarOnCreation);
    }
  };

  const updateRequiredField = (name, setName, fieldName) => {
    if (name.trim() === '') {
      setName(requiredFields[fieldName]);
      return;
    }
    autoSaveHandler();
  };

  const renderProjectSettingsForm = () => {
    return (
      <>
        {isFormLoaded ? (
          <>
            <section
              className="settings_project_modal__content"
              id="project-modal-body"
            >
              <APFormGroup grid={false}>
                <APFormGroup>
                  <FormSelect
                    name="Project Type"
                    value={projectType}
                    onChange={(e) => {
                      setProjectType(e.target.value);
                    }}
                    disabled={!!weddingId}
                    required
                  >
                    {PROJECT_TYPES.map((option, index) => (
                      <option value={option.value}
                        key={`option_${index}`}
                      >
                        {option.label}
                      </option>
                    ))}
                  </FormSelect>
                  {!weddingId && (
                    <FormSelect
                      name="Template"
                      required
                      value={getTemplateValue()}
                      showError={!isFormValid && !projectTemplate}
                      onChange={(e) => {
                        const { value } = e.target;
                        if (value === '__default__') {
                          setProjectTemplate(value);
                        } else {
                          setProjectTemplate(parseInt(value, 10));
                        }
                        setError('');
                      }}
                    >
                      {(getTemplateItems()).map((option, index) => (
                        <option value={option.value}
                          key={`option_${index}`}
                        >
                          {option.label}
                        </option>
                      ))}
                    </FormSelect>
                  )}
                </APFormGroup>
                {renderProjectName()}
              </APFormGroup>
            </section>
            {projectType === 'wedding' && renderParty()}
            {renderImage()}
            {renderDetails()}
            {weddingId === null ||
              weddingId === '' ||
              (formData?.organizationId && userInfo?.orgId === formData?.organizationId)
              ? renderSettings()
              : null
            }
            {!weddingId && (
              <div className="justify-content-center">
                <section className="settings_project_modal__error">
                  {error}
                </section>
                <button
                  className="settings_project_modal__create button"
                  onClick={handleProjectSettingsCreate}
                >
                  Create project
                </button>
              </div>
            )}
          </>
        ) : (
          <section className="settings_project_modal__app_loader">
            <FadeLoader
              color={styledTheme.lighterGray}
              loading
              height={15}
              width={5}
              radius={5}
              margin={2}
            />
          </section>
        )}
        <ChecklistDatesModal
          open={openChecklistDates}
          onClose={() => {
            setOpenChecklistDates(false);
            setOriginalProjectDate(dayjs(eventDate).format(DEFAULT_DATE_FORMAT));
            setChecklistDatesUpdate(false);
          }}
          weddingId={weddingId}
          originalDate={originalProjectDate}
        />
        <ErrorModal
          open={openErrorModal}
          onClose={() => {
            setErrorModalMessage(null);
            setOpenErrorModal(false);
          }}
        >
          <div>{errorModalMessage}</div>
        </ErrorModal>
        <APModal
          open={openPermissionsErrorModal}
          onClose={() => setOpenPermissionsErrorModal(false)}
          name="project-action-confirm"
          title="Heads up!"
        >
          <div className="content-container content-container--narrow">
            <div>
              It looks like you do not have permission to add service types. Please contact the owner of your account.
            </div>
          </div>
          <button
            className="button"
            onClick={() => setOpenPermissionsErrorModal(false)}
          >
            Got it
          </button>
        </APModal>
      </>
    );
  };

  const renderParty = () => {
    return (
      <>
        <section className="settings_project_modal__content">
          <APFormGroup
            title="The Couple"
            gridGap={1}
          >
            <APFormGroup
              direction="vertical"
            >
              <FormSelect
                id="couple-role-1"
                name="Role"
                value={role1}
                onChange={(e) => {
                  setRole1(e.target.value);
                }}
                required
              >
                {PROJECT_PARTY_ROLES.map((option, index) => (
                  <option value={option.value}
                    key={`option_${index}`}
                  >
                    {option.label}
                  </option>
                ))}
              </FormSelect>
              <InputField
                name="first_name_1"
                label="First Name"
                value={firstName1}
                onChange={(event) => {
                  setFirstName1(event.target.value);
                  setError('');
                  if (projectName === `${firstName1} & ${firstName2}'s Wedding` || projectName === '') {
                    setProjectName(`${event.target.value} & ${firstName2}'s Wedding`);
                  }
                }}
                showError={!isFormValid && !firstName1}
                onBlur={() => {
                  updateRequiredField(firstName1, setFirstName1, 'firstName1');
                }}
                required
              />
              <InputField
                name="last_name_1"
                label="Last Name"
                value={lastName1}
                onChange={(event) => {
                  setLastName1(event.target.value);
                  setError('');
                }}
                showError={!isFormValid && !lastName1}
                onBlur={() => {
                  updateRequiredField(lastName1, setLastName1, 'lastName1');
                }}
                required
              />
            </APFormGroup>
            <div className="party-separator">
              <HeaderString
                value="&"
                color="gray"
              />
            </div>
            <APFormGroup
              direction="vertical"
            >
              <FormSelect
                id="couple-role-2"
                name="Role"
                value={role2}
                onChange={(e) => {
                  setRole2(e.target.value);
                }}
                required
              >
                {PROJECT_PARTY_ROLES.map((option, index) => (
                  <option value={option.value}
                    key={`option_${index}`}
                  >
                    {option.label}
                  </option>
                ))}
              </FormSelect>
              <InputField
                name="first_name_2"
                label="First Name"
                value={firstName2}
                onChange={(event) => {
                  setFirstName2(event.target.value);
                  setError('');
                  if (projectName === `${firstName1} & ${firstName2}'s Wedding` || projectName === '') {
                    setProjectName(`${firstName1} & ${event.target.value}'s Wedding`);
                  }
                }}
                showError={!isFormValid && !firstName2}
                onBlur={() => {
                  updateRequiredField(firstName2, setFirstName2, 'firstName2');
                }}
                required
              />
              <InputField
                name="last_name_2"
                label="Last Name"
                value={lastName2}
                onChange={(event) => {
                  setLastName2(event.target.value);
                  setError('');
                }}
                showError={!isFormValid && !lastName2}
                onBlur={() => {
                  updateRequiredField(lastName2, setLastName2, 'lastName2');
                }}
                required
              />
            </APFormGroup>
          </APFormGroup>
        </section>
      </>
    );
  };

  const renderProjectName = () => {
    return (
      <APFormGroup>
        <InputField
          name="project_name"
          label="Project Name"
          value={projectName}
          onChange={(event) => {
            setProjectName(event.target.value);
            setError('');
          }}
          showError={!isFormValid && !projectName}
          onBlur={() => {
            updateRequiredField(projectName, setProjectName, 'projectName');
          }}
          required
        />
        {!weddingId && (
          <FormSelect
            name="Service Type"
            value={serviceType}
            emptyValue="Select Service Type…"
            onChange={(e) => {
              onChangeServiceType(e.target.value);
            }}
          >
            {serviceTypes.map((option, index) => (
              <option value={option.value}
                key={`option_${index}`}
              >
                {option.label}
              </option>
            ))}
          </FormSelect>
        )}
      </APFormGroup>
    );
  };

  const renderDetails = () => {
    return (
      <>
        <section className="settings_project_modal__content">
          <APFormGroup
            title="Project Details"
            grid={false}
          >
            <APFormGroup>
              <CalendarWidget
                name="Event Date"
                date={dayjs(eventDate).format(dateFormatLabel)}
                pickerDate={dayjs(eventDate).format(DEFAULT_DATE_FORMAT)}
                dateFormatLabel={dateFormatLabel}
                setDate={onDateViewChange}
                showError={showDateError}
                showNextYears
                hasClear={false}
                onPickerClose={() => {
                  setChecklistDatesUpdate(true);
                }}
                onBlur={(e) => {
                  onDateViewChange(e.target.value);
                  if (showDateError) {
                    // Reset form input to original date.
                    e.target.value = dayjs(originalProjectDate, DEFAULT_DATE_FORMAT).format(dateFormatLabel);
                    setShowDateError(false);
                    if (pushNotification) {
                      pushNotification({
                        message: 'Date entered was invalid and was not saved.',
                        time: 3000,
                      });
                    }
                  } else {
                    setChecklistDatesUpdate(true);
                  }
                }}
                // #TODO: This was commented out, but it breaks manually typing in date.
                // defaultValue={dayjs(eventDate).format(dateFormatLabel)}
                required
              />
              <FormSelect
                name="Time Zone"
                value={timeZone}
                onChange={(e) => {
                  setTimeZone(e.target.value);
                }}
                required
                showSearch
                showError={!isFormValid && !timeZone}
              >
                {TIME_ZONES_ORDER.map((option, index) => (
                  <optgroup label={option.value}
                    key={`option_${index}`}
                  >
                    {TIME_ZONES.filter((tz) => tz.group === option.value).map(
                      (optionTZ, indexTZ) => (
                        <option
                          value={optionTZ.value}
                          key={`optionTZ_${option.value}_${indexTZ}`}
                        >
                          {optionTZ.label}
                        </option>
                      ),
                    )}
                  </optgroup>
                ))}
              </FormSelect>
            </APFormGroup>
            <div className="view_option view_option--enabled view_option--all-day">
              <Clickwrap
                name="all-day-event"
                checked={allDay}
                className="clickwrap--view-option small"
                onChange={() => handleTimeChange({ allDay: !allDay })}
              />
              <label htmlFor="all-day-event">
                All day event
              </label>
            </div>
            {!allDay ? (
              <APFormGroup>
                <TimeSettingFormItem
                  // Refresh widget when data changes.
                  key={`start-time-${startTime}-${timeFormat}-${timeInputsCounter}`}
                  name="Start time"
                  setTime={(value) => handleTimeChange({ start: value })}
                  time={startTime ?? '9:00'}
                  timeFormat={timeFormat}
                  timeList={timeList}
                />
                <TimeSettingFormItem
                  // Refresh widget when data changes.
                  key={`end-time-${endTime}-${timeFormat}-${timeInputsCounter}`}
                  name="End time"
                  setTime={(value) => handleTimeChange({ end: value })}
                  time={endTime ?? '10:00'}
                  timeList={timeList}
                  timeFormat={timeFormat}
                  error={!isFormValid && !allDay && !endTime}
                />
              </APFormGroup>
            ) : null}
            <APFormGroup>
              <InputField
                name="venue_name"
                label="Venue"
                value={venueName}
                onChange={(e) => {
                  setVenueName(e.target.value);
                }}
                onBlur={() => {
                  autoSaveHandler();
                }}
              />
              <LocationSelect
                location={location}
                setLocation={setLocation}
              />
            </APFormGroup>
            <APFormGroup>
              <InputField
                name="guest_count"
                label="Guest Count"
                value={guestCountView}
                onChange={(event) => {
                  setGuestCountView(event.target.value);
                }}
                onBlur={() => {
                  onGuestCountViewChange();
                }}
              />
              {!weddingId && (
                <InputField
                  name="budget"
                  label="Budget"
                  value={budgetView}
                  onChange={(event) => {
                    setBudgetView(event.target.value);
                  }}
                  onBlur={() => {
                    onBudgetViewChange();
                  }}
                />
              )}
            </APFormGroup>
          </APFormGroup>
        </section>
      </>
    );
  };

  const onChangeServiceType = (value) => {
    if (value !== '-1') {
      setServiceType(value);
    } else if (userInfo.permissions &&
      userInfo.permissions['settings.lead.edit'] &&
      userInfo.permissions['settings.lead.view']) {
      window.location.href = `/app/pro/${orgId}/settings/leads`;
    } else {
      setOpenPermissionsErrorModal(true);
    }
  };

  const renderImage = () => {
    return (
      <section className="settings_project_modal__content">
        <APFormGroup
          title="Cover Photo"
          className="ap-form-group--flush-title"
          grid={false}
        >
          <ImageUploadField
            labelHidden
            imageUrl={profileImageUrl}
            originalImageKey={originalProfileImageKey}
            updateImageKey={setProfileImageKey}
            updateImageUrl={setProfileImage}
            updateOriginalImageKey={setOriginalProfileImageKey}
            imageInfoPath='wedding/profile_images'
            imageProcessPath='wedding/profile_image'
            imageUploadText='Upload a cover photo for this project by dragging an image here'
          />
        </APFormGroup>
      </section>
    );
  };

  const renderSettings = () => {
    return (
      <section className="settings_project_modal__content">
        <APFormGroup
          title="Settings"
          grid={false}
        >
          <FormItem
            id="enabled-features"
            name="Enabled Features"
            type="multi-checkbox"
          >
            <div className="settings_project_modal__features">
              <div className="settings_project_modal__features-column">
                {features.slice(0, featuresColumnLen).map((option, key) => {
                  return (
                    <div
                      key={option.id}
                      className="view_option view_option--enabled"
                    >
                      <Clickwrap
                        name={`features-field--${option.id}`}
                        checked={option.checked}
                        className="clickwrap--view-option small settings_project_modal__features-checkbox"
                        onChange={() => onFeaturesCheckChange(key)}
                      />
                      <label htmlFor={`features-field--${option.id}`}>
                        {option.name}
                      </label>
                    </div>
                  );
                })}
              </div>
              <div className="settings_project_modal__features-column">
                {features
                  .slice(featuresColumnLen, featuresColumnLen * 2)
                  .map((option, key) => {
                    return (
                      <div
                        key={option.id}
                        className="view_option view_option--enabled"
                      >
                        <Clickwrap
                          name={`features-field--${option.id}`}
                          checked={option.checked}
                          className="clickwrap--view-option small settings_project_modal__features-checkbox"
                          onChange={() =>
                            onFeaturesCheckChange(featuresColumnLen + key)
                          }
                        />
                        <label htmlFor={`features-field--${option.id}`}>
                          {option.name}
                        </label>
                      </div>
                    );
                  })}
              </div>
              <div className="settings_project_modal__features-column">
                {features
                  .slice(featuresColumnLen * 2, featuresColumnLen * 3)
                  .map((option, key) => {
                    return (
                      <div
                        key={option.id}
                        className="view_option view_option--enabled"
                      >
                        <Clickwrap
                          name={`features-field--${option.id}`}
                          checked={option.checked}
                          className="clickwrap--view-option small settings_project_modal__features-checkbox"
                          onChange={() =>
                            onFeaturesCheckChange(featuresColumnLen * 2 + key)
                          }
                        />
                        <label htmlFor={`features-field--${option.id}`}>
                          {option.name}
                        </label>
                      </div>
                    );
                  })}
              </div>
            </div>
          </FormItem>
          {integratedStatus === 'connected' && validIntegration && (
            <FormItem
              id="calendar-sync"
              name="Calendar Sync"
              type="checkbox"
            >
              <div className="settings_project_modal__features">
                <div
                  key="calendar-sync"
                  className="view_option view_option--enabled"
                >
                  <Clickwrap
                    name="calendar-sync-clickwrap"
                    checked={syncCalendarOnCreation || isSynced}
                    onChange={handleCalendarSync}
                    className="clickwrap--view-option small settings_project_modal__features-checkbox"
                  />
                  <label htmlFor="calendar-sync-clickwrap">
                    {`Sync to ${displayProvider} calendar`}
                  </label>
                </div>
              </div>
            </FormItem>
          )}
          <APFormGroup>
            <FormSelect
              name="Currency"
              defaultValue="USD"
              value={currency}
              showSearch
              onChange={(e) => {
                setCurrency(e.target.value);
              }}
            >
              {CURRENCIES.map((option, index) => (
                <option value={option.value}
                  key={`option_${index}`}
                >
                  {option.label}
                </option>
              ))}
            </FormSelect>
            <FormSelect
              name="Date Format"
              defaultValue={DATE_FORMATS[0].value}
              value={dateFormat}
              onChange={(e) => {
                const newDateFormatLabel = DATE_FORMATS.find(format => format.value === e.target.value).label;
                setDateFormatLabel(newDateFormatLabel);
                setDateFormat(e.target.value);
                updateEventDateView(newDateFormatLabel);
              }}
            >
              {DATE_FORMATS.map((option, index) => (
                <option value={option.value}
                  key={`option_${index}`}
                >
                  {option.label}
                </option>
              ))}
            </FormSelect>
            <FormSelect
              name="Time"
              defaultValue="12"
              value={timeFormat}
              onChange={(e) => {
                setTimeFormat(e.target.value);
                setTimeList(getTimeList((e.target.value)));
              }}
            >
              {TIME_FORMATS.map((option, index) => (
                <option value={option.value}
                  key={`option_${index}`}
                >
                  {`${option.label.replace('h', 'H')}s`}
                </option>
              ))}
            </FormSelect>
          </APFormGroup>
        </APFormGroup>
      </section>
    );
  };

  return renderProjectSettingsForm();
};
ProjectsSettingsDetails.propTypes = {
  type: PropTypes.number,
  formData: PropTypes.object,
  updateWeddingChanges: PropTypes.func,
  pushNotification: PropTypes.func,
};

ProjectsSettingsDetails.defaultProps = {};

export default ProjectsSettingsDetails;
